/**
 * Filter panel shown before an export is generated.
 *
 * Two columns:
 *  - Assets: every exportable item (pre-filtered by the caller) grouped by
 *    hexagon. The user can toggle individual items or entire hexagons on/off.
 *    Hexagons where every item is deselected are marked "Exclu — aucun asset".
 *  - Hexes: every active hexagon. Deselecting a hex excludes it from the
 *    export bounding box, regardless of whether it contains assets.
 */

import { useEffect, useMemo, useRef } from 'react';
import { getIconEntry } from '../lib/icon-catalog.js';
import { getArrowColor } from '../lib/category-colors.js';
import {
  ASSMAT_SHORT,
  MSUP_VARIANT_SHORT,
} from '../lib/fac-build-msup-export.js';
import type { AvailableHex, ExportFilterItem } from '../lib/war-plan-export.js';

/**
 * Returns the underlying MapObject ids that participate in `excludedIds`
 * for the given item. Fusions span multiple ids; objects/arrows expose a
 * single id (their own).
 */
function itemObjectIds(item: ExportFilterItem): readonly string[] {
  return item.type === 'fusion' ? item.objectIds : [item.id];
}

/** True when the item should appear in the final export (i.e. at least one
 * underlying id is NOT excluded). */
function isItemIncluded(item: ExportFilterItem, excludedIds: ReadonlySet<string>): boolean {
  return itemObjectIds(item).some((id) => !excludedIds.has(id));
}

interface HexGroup {
  hexKey: string | null;
  hexLabel: string;
  items: ExportFilterItem[];
}

export interface ExportFilterPanelProps {
  items: ExportFilterItem[];
  excludedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  /** Called with all item ids in the group so the modal can toggle the whole hex. */
  onToggleHex: (ids: string[]) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  /** All hexagons available for the export (admin-active). */
  availableHexes: AvailableHex[];
  /** Hex keys manually deselected by the user in the right column. */
  excludedHexKeys: ReadonlySet<string>;
  onToggleHexKey: (key: string) => void;
  onSelectAllHexes: () => void;
  onDeselectAllHexes: () => void;
}

export function ExportFilterPanel({
  items,
  excludedIds,
  onToggle,
  onToggleHex,
  onSelectAll,
  onDeselectAll,
  availableHexes,
  excludedHexKeys,
  onToggleHexKey,
  onSelectAllHexes,
  onDeselectAllHexes,
}: ExportFilterPanelProps): React.JSX.Element {
  const groups = useMemo<HexGroup[]>(() => {
    const map = new Map<string, HexGroup>();
    for (const item of items) {
      const key = item.hexKey ?? '__no_region__';
      if (!map.has(key)) {
        map.set(key, { hexKey: item.hexKey, hexLabel: item.hexLabel, items: [] });
      }
      map.get(key)!.items.push(item);
    }
    return [...map.values()];
  }, [items]);

  const includedCount = items.filter((i) => isItemIncluded(i, excludedIds)).length;
  const totalCount = items.length;
  const includedHexCount = availableHexes.filter((h) => !excludedHexKeys.has(h.key)).length;
  const totalHexCount = availableHexes.length;

  // Number of currently-included rows per hex (objects + fusions + arrows
  // whose underlying ids are not all in `excludedIds`). Decreases live as the
  // user toggles items on the left, warning before excluding a hex that still
  // has content.
  const assetCountByHex = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) {
      if (item.hexKey === null) continue;
      if (!isItemIncluded(item, excludedIds)) continue;
      map.set(item.hexKey, (map.get(item.hexKey) ?? 0) + 1);
    }
    return map;
  }, [items, excludedIds]);

  return (
    <div className="grid h-full grid-cols-2 divide-x divide-slate-700">
      {/* ============================================================ */}
      {/* Column 1 — Assets                                            */}
      {/* ============================================================ */}
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-700 px-4 py-2">
          <h3 className="text-sm font-semibold text-slate-200">Assets</h3>
          <span className="text-xs text-slate-400">
            <span className={includedCount === 0 ? 'text-amber-400' : 'text-emerald-400'}>
              {includedCount}
            </span>
            <span className="text-slate-500"> / {totalCount}</span>
          </span>
          <div className="ml-auto flex gap-1">
            <button
              type="button"
              onClick={onSelectAll}
              className="rounded px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100"
            >
              Tout
            </button>
            <button
              type="button"
              onClick={onDeselectAll}
              className="rounded px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100"
            >
              Aucun
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {groups.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-500">Aucun élément à exporter.</p>
          )}

          {groups.map((group) => {
            const groupIds = group.items.flatMap(itemObjectIds);
            const excludedCount = groupIds.filter((id) => excludedIds.has(id)).length;
            const allExcluded = excludedCount === groupIds.length;
            const allIncluded = excludedCount === 0;
            const indeterminate = !allExcluded && !allIncluded;

            return (
              <HexGroupRow
                key={group.hexKey ?? '__no_region__'}
                group={group}
                allExcluded={allExcluded}
                allIncluded={allIncluded}
                indeterminate={indeterminate}
                excludedIds={excludedIds}
                onToggle={onToggle}
                onToggleHex={onToggleHex}
              />
            );
          })}
        </div>
      </div>

      {/* ============================================================ */}
      {/* Column 2 — Hexagons                                          */}
      {/* ============================================================ */}
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-700 px-4 py-2">
          <h3 className="text-sm font-semibold text-slate-200">Hexagones</h3>
          <span className="text-xs text-slate-400">
            <span className={includedHexCount === 0 ? 'text-amber-400' : 'text-emerald-400'}>
              {includedHexCount}
            </span>
            <span className="text-slate-500"> / {totalHexCount}</span>
          </span>
          <div className="ml-auto flex gap-1">
            <button
              type="button"
              onClick={onSelectAllHexes}
              className="rounded px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100"
            >
              Tout
            </button>
            <button
              type="button"
              onClick={onDeselectAllHexes}
              className="rounded px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100"
            >
              Aucun
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {availableHexes.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-500">Aucun hexagone actif.</p>
          )}
          {availableHexes.map((hex) => {
            const included = !excludedHexKeys.has(hex.key);
            const assetCount = assetCountByHex.get(hex.key) ?? 0;
            return (
              <label
                key={hex.key}
                className={`flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-800 ${
                  included ? 'text-slate-200' : 'text-slate-500'
                }`}
              >
                <input
                  type="checkbox"
                  checked={included}
                  onChange={() => onToggleHexKey(hex.key)}
                  className="accent-emerald-500"
                />
                <span>{hex.label}</span>
                <span
                  className={`text-xs ${
                    assetCount === 0
                      ? 'text-slate-600'
                      : included
                        ? 'text-emerald-400'
                        : 'text-amber-400'
                  }`}
                  title={`${assetCount} asset${assetCount > 1 ? 's' : ''} inclus dans cet hexagone`}
                >
                  ({assetCount})
                </span>
                {!included && assetCount > 0 && (
                  <span className="ml-auto rounded bg-amber-900/50 px-1.5 py-0.5 text-xs font-normal text-amber-400">
                    Exclu
                  </span>
                )}
                {!included && assetCount === 0 && (
                  <span className="ml-auto rounded bg-slate-800 px-1.5 py-0.5 text-xs font-normal text-slate-500">
                    Exclu
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

interface HexGroupRowProps {
  group: HexGroup;
  allExcluded: boolean;
  allIncluded: boolean;
  indeterminate: boolean;
  excludedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onToggleHex: (ids: string[]) => void;
}

function HexGroupRow({
  group,
  allExcluded,
  allIncluded,
  indeterminate,
  excludedIds,
  onToggle,
  onToggleHex,
}: HexGroupRowProps): React.JSX.Element {
  const hexCheckRef = useRef<HTMLInputElement | null>(null);
  const groupIds = group.items.flatMap(itemObjectIds);

  // `indeterminate` is a DOM property, not a React prop — sync it via effect.
  useEffect(() => {
    if (hexCheckRef.current) {
      hexCheckRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <div className="mb-3">
      {/* Hex header */}
      <label
        className={`flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1 text-sm font-semibold hover:bg-slate-800 ${
          allExcluded ? 'text-slate-500' : 'text-slate-200'
        }`}
      >
        <input
          type="checkbox"
          ref={hexCheckRef}
          checked={allIncluded}
          onChange={() => onToggleHex(groupIds)}
          className="accent-emerald-500"
        />
        {group.hexLabel}
        {allExcluded && (
          <span className="ml-auto rounded bg-amber-900/50 px-1.5 py-0.5 text-xs font-normal text-amber-400">
            Exclu — aucun asset
          </span>
        )}
      </label>

      {/* Item rows */}
      <div className="ml-4 mt-0.5 space-y-0.5">
        {group.items.map((item) => {
          const included = isItemIncluded(item, excludedIds);
          const handleToggle =
            item.type === 'fusion' ? () => onToggleHex([...item.objectIds]) : () => onToggle(item.id);
          return (
            <label
              key={item.id}
              className={`flex cursor-pointer select-none items-center gap-2 rounded px-2 py-0.5 text-xs hover:bg-slate-800 ${
                included ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              <input
                type="checkbox"
                checked={included}
                onChange={handleToggle}
                className="accent-emerald-500"
              />
              {item.type === 'object' && <ObjectItemBody item={item} included={included} />}
              {item.type === 'arrow' && <ArrowItemBody item={item} included={included} />}
              {item.type === 'fusion' && <FusionItemBody item={item} included={included} />}
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item body sub-components
// ---------------------------------------------------------------------------

function ObjectItemBody({
  item,
  included,
}: {
  item: Extract<ExportFilterItem, { type: 'object' }>;
  included: boolean;
}): React.JSX.Element {
  const iconEntry = getIconEntry(item.obj.kind, item.obj.metadata.iconId);
  const note = item.obj.metadata.note;
  return (
    <>
      <img
        src={iconEntry.src}
        alt=""
        aria-hidden="true"
        className={`h-4 w-4 object-contain ${included ? '' : 'opacity-25'}`}
      />
      {note ? (
        <span className="truncate" title={note}>
          {note}{' '}
          <span className="italic text-slate-500">
            -- {item.minorLabel ?? '—'} ({iconEntry.label})
          </span>
        </span>
      ) : (
        <span>{iconEntry.label}</span>
      )}
    </>
  );
}

function ArrowItemBody({
  item,
  included,
}: {
  item: Extract<ExportFilterItem, { type: 'arrow' }>;
  included: boolean;
}): React.JSX.Element {
  const color = getArrowColor(item.arrow);
  const note = item.arrow.metadata.note;
  return (
    <>
      <svg
        viewBox="0 0 16 16"
        aria-hidden="true"
        className={`h-4 w-4 shrink-0 ${included ? '' : 'opacity-25'}`}
      >
        <defs>
          <marker
            id={`fp-ah-${item.id}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="4"
            markerHeight="4"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
          </marker>
        </defs>
        <line
          x1="1"
          y1="14"
          x2="14"
          y2="2"
          stroke={color}
          strokeWidth="2"
          strokeDasharray={item.arrow.dashed ? '3 2' : undefined}
          markerEnd={`url(#fp-ah-${item.id})`}
          markerStart={item.arrow.arrowheadStart ? `url(#fp-ah-${item.id})` : undefined}
        />
      </svg>
      {note ? (
        <span className="truncate" title={note}>
          {note}{' '}
          <span className="italic text-slate-500">-- {item.minorLabel ?? '—'} (Flèche)</span>
        </span>
      ) : (
        <span>
          Flèche{' '}
          <span className="italic text-slate-500">-- {item.minorLabel ?? '—'}</span>
        </span>
      )}
    </>
  );
}

function FusionItemBody({
  item,
  included,
}: {
  item: Extract<ExportFilterItem, { type: 'fusion' }>;
  included: boolean;
}): React.JSX.Element {
  const iconEntry = getIconEntry(item.primary.kind, item.primary.metadata.iconId);
  const note = item.primary.metadata.note;
  // Compose badges from secondaries
  const badges = item.secondaries
    .map((s) => {
      if (item.fusionKind === 'msup') return MSUP_VARIANT_SHORT[s.metadata.iconId] ?? null;
      return ASSMAT_SHORT[s.metadata.iconId] ?? null;
    })
    .filter((b): b is string => b !== null);
  const badgeText = badges.length > 0 ? ` + ${badges.join(' + ')}` : '';
  const baseLabel = note && note.trim().length > 0 ? note : iconEntry.label;
  return (
    <>
      <img
        src={iconEntry.src}
        alt=""
        aria-hidden="true"
        className={`h-4 w-4 object-contain ${included ? '' : 'opacity-25'}`}
      />
      <span className="truncate" title={baseLabel + badgeText}>
        {baseLabel}
        <span className="text-emerald-400">{badgeText}</span>{' '}
        <span className="italic text-slate-500">
          -- {item.minorLabel ?? '—'} ({iconEntry.label})
        </span>
      </span>
    </>
  );
}
